import { ref } from 'vue';
import { useNotification } from '@asabdev/asab-ui';
import { modelGatewayApi } from '../api';
import { toUserMessage } from '@/lib/api/errors';
import type {
  CustomModelConnectionResponse,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  NormalisedModelItem,
  ImportRequest,
  ImportResponse,
} from '../types';

/**
 * Composable for managing custom model connections
 */
interface NotificationApi {
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

export function useConnections() {
  let notification: NotificationApi;
  try {
    notification = useNotification();
  } catch (e) {
    console.warn('useNotification failed, using fallback', e);
    notification = {
      showSuccess: (msg: string) => console.log('Success:', msg),
      showError: (msg: string) => console.error('Error:', msg),
    };
  }
  const { showSuccess, showError } = notification;

  // State
  const connections = ref<CustomModelConnectionResponse[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch all connections for the organization
   */
  const fetchConnections = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await modelGatewayApi.listConnections();
      connections.value = response.data;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to fetch connections');
      error.value = message;
      console.error('Error fetching connections:', err);
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new connection
   */
  const createConnection = async (data: CreateConnectionRequest) => {
    isLoading.value = true;
    try {
      const result = await modelGatewayApi.createConnection(data);
      showSuccess(`Successfully created connection "${result.name}"`);
      await fetchConnections();
      return result;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to create connection');
      showError(message);
      console.error('Error creating connection:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing connection
   */
  const updateConnection = async (slug: string, data: UpdateConnectionRequest) => {
    isLoading.value = true;
    try {
      const result = await modelGatewayApi.updateConnection(slug, data);
      showSuccess(`Successfully updated connection "${result.name}"`);
      await fetchConnections();
      return result;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to update connection');
      showError(message);
      console.error('Error updating connection:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a connection
   */
  const deleteConnection = async (slug: string) => {
    isLoading.value = true;
    try {
      await modelGatewayApi.deleteConnection(slug);
      showSuccess('Connection deleted successfully');
      await fetchConnections();
      return true;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to delete connection');
      showError(message);
      console.error('Error deleting connection:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const listConnectionModels = async (slug: string): Promise<NormalisedModelItem[]> => {
    try {
      const response = await modelGatewayApi.listConnectionModels(slug);
      return response.data;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to fetch upstream models');
      showError(message);
      return [];
    }
  };

  const importModels = async (slug: string, data: ImportRequest): Promise<ImportResponse | null> => {
    isLoading.value = true;
    try {
      const response = await modelGatewayApi.importConnectionModels(slug, data);
      showSuccess(`Successfully imported ${response.imported.length} models`);
      return response;
    } catch (err: unknown) {
      const message = toUserMessage(err, 'Failed to import models');
      showError(message);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    connections,
    isLoading,
    error,
    fetchConnections,
    createConnection,
    updateConnection,
    deleteConnection,
    listConnectionModels,
    importModels,
  };
}
